import Plot from 'features/plotly'
import _ from 'lodash'
import { Data, Layout } from 'plotly.js'
import React, { FC, useMemo } from 'react'
import CantileverTheme from 'styles/cantileverTheme'

const CURVE_COLOR = CantileverTheme.colors.highVis
const NUM_CURVE_POINTS = 100

const PLOTLY_FG_COLOR = CantileverTheme.colors.grayscale[100]
const PLOTLY_FONT = {
  color: PLOTLY_FG_COLOR,
}

export interface FfsAcceptabilityCriteriaCurveProps {
  lambda: number | null
  rsf: number | null
  rt: number | null
  curveData: (Data & { x: number[]; y: number[] })[] | null
  passesFac: boolean
}

function computeRt(lambda: number, rsfa: number, mt: number): number {
  if (lambda <= 0.354) {
    return 0.2
  }
  if (lambda >= 20) {
    return 0.9
  }

  if (mt === 0) {
    return 0
  }

  const rsfaOverMt = rsfa / mt
  const part1 = rsfa - rsfaOverMt
  const part2 = Math.pow(1.0 - rsfaOverMt, -1)

  return part1 * part2
}

function useAcceptabilityCurveData(
  lambdaRange: [number, number] | null,
  rsfa: number | null,
  mt: number | null,
  color: string
): (Data & { x: number[]; y: number[] })[] | null {
  return useMemo(() => {
    if (_.isNil(lambdaRange) || _.isNil(rsfa) || _.isNil(mt)) {
      return null
    }
    const [lambMin, lambMax] = lambdaRange
    const lambdaVals = _.range(
      lambMin,
      lambMax,
      (lambMax - lambMin) / NUM_CURVE_POINTS
    )
    return [
      {
        x: lambdaVals,
        y: lambdaVals.map((lambda) => computeRt(lambda, rsfa, mt)),
        mode: 'line',
        name: 'Acceptability Curve',
        marker: { color },
      },
    ]
  }, [color, lambdaRange, mt, rsfa])
}

function useAcceptabilityCurveLayout(
  rt: number | null,
  maxRt: number | null
): Partial<Layout> {
  return useMemo(() => {
    const yRange = [
      Math.min(0, rt ?? 0),
      Math.max(1.1, rt ?? 1.1, maxRt ?? 1.1),
    ]
    const [yMin, yMax] = yRange
    return {
      xaxis: {
        title: {
          text: 'λ',
          font: PLOTLY_FONT,
        },
        tickfont: PLOTLY_FONT,
        dtick: 4,
      },
      yaxis: {
        title: {
          text: 'R_t',
          font: PLOTLY_FONT,
        },
        dtick: +((yMax - yMin) / 4).toFixed(1),
        range: yRange,
        tickfont: PLOTLY_FONT,
      },
      showlegend: false, // Hide the legend
      hovermode: 'closest', // Enable hover info when cursor is closest to data
      paper_bgcolor: 'rgba(0, 0, 0, 0)', // Background of the entire graph container
      plot_bgcolor: 'rgba(0, 0, 0, 0)', // Background of the plotting area
      margin: {
        l: 50, // Left margin
        r: 50, // Right margin
        b: 100, // Bottom margin
        t: 10, // Top margin
      },
    }
  }, [maxRt, rt])
}

type HookProps = { mt: number | undefined; rsfa: number | undefined } & Omit<
  FfsAcceptabilityCriteriaCurveProps,
  'curveData' | 'passesFac'
>

export type CurveDataProps = Omit<
  FfsAcceptabilityCriteriaCurveProps,
  'lambda' | 'rsf'
>

export function useFinalAcceptabilityCurveData({
  mt,
  lambda,
  rt,
  rsfa,
}: HookProps): CurveDataProps {
  const curveData = useAcceptabilityCurveData(
    [Math.min(0, lambda ?? 0), Math.max(lambda ?? 25, 25)],
    rsfa ?? null,
    mt ?? null,
    CURVE_COLOR
  )

  const rtThresh = useMemo(() => {
    if (_.isNil(curveData)) {
      return null
    }
    const xVals = curveData[0]['x']
    const xIdx = Math.max(_.sortedIndex(xVals, lambda) - 1, 0)
    const yVals = curveData[0]['y']
    return yVals[xIdx]
  }, [curveData, lambda])

  const passesFac = !_.isNil(rt) && !_.isNil(rtThresh) && rt > rtThresh

  return useMemo(
    () => ({
      curveData: curveData ?? null,
      passesFac,
      rt,
    }),
    [curveData, passesFac, rt]
  )
}

const RawFfsAcceptabilityCriteriaCurve: FC<
  FfsAcceptabilityCriteriaCurveProps
> = ({ lambda, rt, curveData, passesFac }) => {
  const maxRt: number = useMemo(() => {
    const yVals = curveData?.[0]['y']
    return _.max(yVals ?? []) as number
  }, [curveData])
  const layout = useAcceptabilityCurveLayout(rt, maxRt)

  const data = useMemo<Data[]>(
    () => [
      ...(curveData ?? []),
      {
        x: [lambda],
        y: [rt],
        type: 'scatter',
        mode: 'markers',
        marker: {
          color: passesFac
            ? CantileverTheme.colors.LimeGreen
            : CantileverTheme.colors.error['400_base'],
          size: 10,
          symbol: 'cross',
          name: 'Asset',
        },
      },
    ],
    [curveData, lambda, rt, passesFac]
  )
  return (
    <Plot
      data={data}
      layout={layout}
      style={{ width: '100%', height: '250px' }}
    />
  )
}

RawFfsAcceptabilityCriteriaCurve.displayName = 'FfsAcceptabilityCriteriaCurve'
export const FfsAcceptabilityCriteriaCurve = React.memo(
  RawFfsAcceptabilityCriteriaCurve
)
